import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Stack } from "@mui/system";
import ScoutIcon from "components/ScoutIcon";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { memo, useCallback, useMemo } from "react";

import { ToolCallGroup } from "../ToolCallGroup";
import { MessageContent } from "./MessageContent";
import {
  getScoutMessageText,
  ScoutChatMessage,
  ScoutChatMessagePart,
  ScoutTextPart,
  ScoutToolPart,
} from "./scoutChatMessage";

type RenderBlock =
  | { kind: "text"; id: string; parts: ScoutTextPart[] }
  | { kind: "tool-group"; id: string; tools: ScoutToolPart[] };

function groupParts(parts: ScoutChatMessagePart[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];

  for (const part of parts) {
    const last = blocks[blocks.length - 1];

    if (part.type === "tool") {
      if (last?.kind === "tool-group") {
        last.tools.push(part);
      } else {
        blocks.push({ kind: "tool-group", id: part.id, tools: [part] });
      }
      continue;
    }

    if (part.type !== "text") {
      continue;
    }

    if (last?.kind === "text") {
      last.parts.push(part);
    } else {
      blocks.push({ kind: "text", id: part.id, parts: [part] });
    }
  }

  return blocks;
}

export const AgentMessage = memo(
  function AgentMessage({ message }: { message: ScoutChatMessage }) {
    const blocks = useMemo(() => groupParts(message.parts), [message.parts]);

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          py: 1.75,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
          sx={{
            maxWidth: "90%",
            px: 2,
            py: 1.5,
            borderRadius: 3,
            bgcolor: "transparent",
            color: "text.primary",
            wordBreak: "break-word",
          }}
        >
          <Box
            sx={{
              "@keyframes scoutSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <ScoutIcon
              sx={{
                color: "#B0BEC5",
                animation: message.isStreaming ? "scoutSpin 1.1s linear infinite" : "none",
                transformOrigin: "center",
                mt: 0.125,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2.25}>
              {blocks.map((block) =>
                block.kind === "tool-group" ? (
                  <ToolCallGroup
                    key={block.id}
                    toolParts={block.tools}
                    isStreaming={message.isStreaming}
                  />
                ) : (
                  <MessageContent
                    key={block.id}
                    messageId={`${message.id}-${block.id}`}
                    parts={block.parts}
                    isStreaming={message.isStreaming}
                  />
                )
              )}
            </Stack>
            <Box sx={{ height: 10 }} />
            <AgentMessageToolbar message={message} />
          </Box>
        </Stack>
      </Box>
    );
  },
  (prev, next) =>
    prev.message.id === next.message.id &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.message.parts === next.message.parts
);
AgentMessage.displayName = "AgentMessage";

function AgentMessageToolbar({ message }: { message: ScoutChatMessage }) {
  const copyToClipboard = useCopyToClipboard();
  const handleCopy = useCallback(async () => {
    const text = getScoutMessageText(message);
    await copyToClipboard(text);
  }, [copyToClipboard, message]);

  if (message.isStreaming) {
    return null;
  }

  return (
    <Stack width="100%" direction="row" alignItems="center" spacing={1} sx={{ ml: -1 }}>
      <Tooltip title="Copy text">
        <IconButton aria-label="Copy text" size="small" onClick={handleCopy}>
          <CopyAllOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
